/**
 * Canonical assessment Task classification helpers.
 *
 * Shared by medplum-server bots and medplum-ui so standing orders, occurrences,
 * one-time assignments, and Take Now Tasks are distinguished the same way.
 */
import type { CodeableConcept, Extension, Questionnaire, Task, Timing } from '@medplum/fhirtypes';

import {
  ASSESSMENT_CANCELLATION_REASON_EXT,
  ASSESSMENT_FREQUENCY_EXTENSION,
  ASSESSMENT_FREQUENCY_SYSTEM,
  ASSESSMENT_NEXT_SEND_EXTENSION,
  ASSESSMENT_RECURRENCE_END_EXTENSION,
  ASSESSMENT_SCHEDULE_INPUT_CODE,
  CLIENT_ASSESSMENT_ASSIGNMENT_CODE,
  CLIENT_TAKE_NOW_ASSESSMENT_CODE,
  TASK_INPUT_SYSTEM,
  TEMPLATE_CATEGORY_SYSTEM,
  TASK_TYPE_SYSTEM,
} from '../constants';

/** Recurring frequency values that mark a standing-order schedule Task. */
export const RECURRING_ASSESSMENT_FREQUENCIES = [
  'Weekly',
  'Biweekly',
  'Monthly',
  'Bimonthly',
  'Quarterly',
  'Every Session',
] as const;

export type RecurringAssessmentFrequency = (typeof RECURRING_ASSESSMENT_FREQUENCIES)[number];
export type AssessmentFrequency = RecurringAssessmentFrequency | 'Once';

export type AssessmentTaskKind =
  | 'standing-order'
  | 'occurrence'
  | 'one-time'
  | 'take-now'
  | 'none';

function hasTaskCode(task: Task, code: string): boolean {
  return (task.code?.coding ?? []).some((c) => c.system === TASK_TYPE_SYSTEM && c.code === code);
}

export function isRecurringAssessmentFrequency(value: string | undefined | null): boolean {
  if (!value) return false;
  return (RECURRING_ASSESSMENT_FREQUENCIES as readonly string[]).includes(value);
}

const FREQUENCY_CODE_BY_LABEL: Record<AssessmentFrequency, string> = {
  Once: 'once',
  'Every Session': 'every-session',
  Weekly: 'weekly',
  Biweekly: 'biweekly',
  Monthly: 'monthly',
  Bimonthly: 'bimonthly',
  Quarterly: 'quarterly',
};

const FREQUENCY_LABEL_BY_CODE = Object.fromEntries(
  Object.entries(FREQUENCY_CODE_BY_LABEL).map(([label, code]) => [code, label]),
) as Record<string, AssessmentFrequency>;

function getScheduleInput(task: Task): NonNullable<Task['input']>[number] | undefined {
  return task.input?.find((input) =>
    input.type?.coding?.some(
      (coding) => coding.system === TASK_INPUT_SYSTEM && coding.code === ASSESSMENT_SCHEDULE_INPUT_CODE,
    ),
  );
}

export function assessmentFrequencyToTiming(frequency: AssessmentFrequency): Timing {
  const timing: Timing = {
    code: {
      coding: [
        {
          system: ASSESSMENT_FREQUENCY_SYSTEM,
          code: FREQUENCY_CODE_BY_LABEL[frequency],
          display: frequency,
        },
      ],
      text: frequency,
    },
  };
  switch (frequency) {
    case 'Weekly':
      timing.repeat = { frequency: 1, period: 1, periodUnit: 'wk' };
      break;
    case 'Biweekly':
      timing.repeat = { frequency: 1, period: 2, periodUnit: 'wk' };
      break;
    case 'Monthly':
      timing.repeat = { frequency: 1, period: 1, periodUnit: 'mo' };
      break;
    case 'Bimonthly':
      timing.repeat = { frequency: 1, period: 2, periodUnit: 'mo' };
      break;
    case 'Quarterly':
      timing.repeat = { frequency: 1, period: 3, periodUnit: 'mo' };
      break;
    default:
      break;
  }
  return timing;
}

function dropEmptyExtensions(task: Task): void {
  task.extension = task.extension?.length ? task.extension : undefined;
}

function labelFromTiming(timing: Timing | undefined): string | undefined {
  const code = timing?.code?.coding?.find((coding) => coding.system === ASSESSMENT_FREQUENCY_SYSTEM)?.code;
  if (code && FREQUENCY_LABEL_BY_CODE[code]) return FREQUENCY_LABEL_BY_CODE[code];
  const labeled = timing?.code?.text ?? timing?.code?.coding?.find((coding) => coding.display)?.display;
  if (labeled && (labeled === 'Once' || isRecurringAssessmentFrequency(labeled))) return labeled;
  return undefined;
}

export function getAssessmentFrequency(task: Task): string | undefined {
  return (
    labelFromTiming(getScheduleInput(task)?.valueTiming) ??
    task.extension?.find((e) => e.url === ASSESSMENT_FREQUENCY_EXTENSION)?.valueString
  );
}

export function getAssessmentNextSend(task: Task): string | undefined {
  return (
    task.restriction?.period?.start ??
    task.extension?.find((e) => e.url === ASSESSMENT_NEXT_SEND_EXTENSION)?.valueDateTime
  );
}

export function getAssessmentRecurrenceEnd(task: Task): string | undefined {
  const legacy = task.extension?.find((e) => e.url === ASSESSMENT_RECURRENCE_END_EXTENSION)?.valueDate;
  // restriction.period.end is recurrence-end only after a native schedule input exists.
  // Before migration it is the SLA due date and must not be treated as recurrence-end.
  if (getScheduleInput(task)) return task.restriction?.period?.end ?? legacy;
  return legacy;
}

export function getAssessmentCancellationReason(task: Task): CodeableConcept | undefined {
  return (
    task.statusReason ??
    task.extension?.find((e) => e.url === ASSESSMENT_CANCELLATION_REASON_EXT)?.valueCodeableConcept
  );
}

export function setAssessmentSchedule(
  task: Task,
  frequency: RecurringAssessmentFrequency,
  nextSend?: string,
  recurrenceEnd?: string,
): void {
  const otherInputs = (task.input ?? []).filter(
    (input) =>
      !input.type?.coding?.some(
        (coding) => coding.system === TASK_INPUT_SYSTEM && coding.code === ASSESSMENT_SCHEDULE_INPUT_CODE,
      ),
  );
  task.input = [
    ...otherInputs,
    {
      type: {
        coding: [
          {
            system: TASK_INPUT_SYSTEM,
            code: ASSESSMENT_SCHEDULE_INPUT_CODE,
            display: 'Assessment recurrence schedule',
          },
        ],
      },
      valueTiming: assessmentFrequencyToTiming(frequency),
    },
  ];
  const period = {
    ...task.restriction?.period,
    ...(nextSend ? { start: nextSend } : {}),
    end: recurrenceEnd,
  };
  if (!period.start) delete period.start;
  if (!period.end) delete period.end;
  task.restriction = {
    ...task.restriction,
    period: period.start || period.end ? period : undefined,
  };
  if (task.restriction && !task.restriction.period) {
    const { period: _period, ...rest } = task.restriction;
    task.restriction = Object.keys(rest).length > 0 ? rest : undefined;
  }
  task.extension = task.extension?.filter(
    (extension) =>
      extension.url !== ASSESSMENT_FREQUENCY_EXTENSION &&
      extension.url !== ASSESSMENT_NEXT_SEND_EXTENSION &&
      extension.url !== ASSESSMENT_RECURRENCE_END_EXTENSION,
  );
  dropEmptyExtensions(task);
}

export function setAssessmentNextSend(task: Task, nextSend: string): void {
  task.restriction = {
    ...task.restriction,
    period: { ...task.restriction?.period, start: nextSend },
  };
  task.extension = task.extension?.filter((extension) => extension.url !== ASSESSMENT_NEXT_SEND_EXTENSION);
  dropEmptyExtensions(task);
}

export function clearAssessmentSchedule(task: Task): void {
  task.input = task.input?.filter(
    (input) =>
      !input.type?.coding?.some(
        (coding) => coding.system === TASK_INPUT_SYSTEM && coding.code === ASSESSMENT_SCHEDULE_INPUT_CODE,
      ),
  );
  if (task.restriction) {
    const { period: _period, ...otherRestriction } = task.restriction;
    task.restriction = Object.keys(otherRestriction).length > 0 ? otherRestriction : undefined;
  }
  if (task.input?.length === 0) task.input = undefined;
  task.extension = task.extension?.filter(
    (extension) =>
      extension.url !== ASSESSMENT_FREQUENCY_EXTENSION &&
      extension.url !== ASSESSMENT_NEXT_SEND_EXTENSION &&
      extension.url !== ASSESSMENT_RECURRENCE_END_EXTENSION,
  );
  dropEmptyExtensions(task);
}

/** Scheduled series assignment (`client-assessment-assignment`). */
export function isScheduledAssessmentTask(task: Task): boolean {
  return hasTaskCode(task, CLIENT_ASSESSMENT_ASSIGNMENT_CODE);
}

/** Provider-administered Take Now (`client-take-now-assessment`). */
export function isTakeNowAssessmentTask(task: Task): boolean {
  return hasTaskCode(task, CLIENT_TAKE_NOW_ASSESSMENT_CODE);
}

/** Any assessment Task (scheduled or Take Now). */
export function isAnyAssessmentTask(task: Task): boolean {
  return isScheduledAssessmentTask(task) || isTakeNowAssessmentTask(task);
}

/**
 * Recurring standing order — schedule configuration only.
 * Never receives Encounter, ChargeItem, QR, Observation, or completion note.
 */
export function isRecurringStandingOrder(task: Task): boolean {
  if (!isScheduledAssessmentTask(task)) return false;
  return isRecurringAssessmentFrequency(getAssessmentFrequency(task));
}

/** Occurrence Task created from a standing order (`partOf` → parent Task). */
export function isAssessmentOccurrenceTask(task: Task): boolean {
  if (!isScheduledAssessmentTask(task)) return false;
  return (task.partOf ?? []).some((ref) => ref.reference?.startsWith('Task/'));
}

/** One-time scheduled assignment (not recurring, not an occurrence child). */
export function isOneTimeAssessmentTask(task: Task): boolean {
  if (!isScheduledAssessmentTask(task)) return false;
  if (isRecurringStandingOrder(task)) return false;
  if (isAssessmentOccurrenceTask(task)) return false;
  return true;
}

/**
 * Individual assessment that should receive Encounter + ChargeItem billing resources.
 * Standing orders are excluded.
 */
export function isBillableAssessmentAssignment(task: Task): boolean {
  if (isTakeNowAssessmentTask(task)) return true;
  if (!isScheduledAssessmentTask(task)) return false;
  return !isRecurringStandingOrder(task);
}

export function classifyAssessmentTask(task: Task): AssessmentTaskKind {
  if (isTakeNowAssessmentTask(task)) return 'take-now';
  if (isRecurringStandingOrder(task)) return 'standing-order';
  if (isAssessmentOccurrenceTask(task)) return 'occurrence';
  if (isScheduledAssessmentTask(task)) return 'one-time';
  return 'none';
}

export function isMeasuresQuestionnaire(q: Questionnaire): boolean {
  const tag = q.meta?.tag?.find((t) => t.system === TEMPLATE_CATEGORY_SYSTEM);
  return tag?.code === 'measures' || tag?.code === 'scored-measures';
}

export function getExt(resource: { extension?: Extension[] }, url: string): Extension | undefined {
  return resource.extension?.find((e) => e.url === url);
}
